<template>
  <div class="popup-form calculation-popup">
    <div class="content-form-popup calculation-popup__content">
      <div class="header-text calculation-popup__header">
        <div class="back-button-wrapper pointer" @click="closePopup">
          <img class="nav-link-icon filter-primary" src="@/images/modal-close.svg" alt="Zavrieť" />
        </div>
        <div>
          <div class="calculation-popup__eyebrow">Vysvetlenie bankovej ponuky</div>
          <h2 class="calculation-popup__title">
            Ako <span class="primary">{{ bankName }}</span> vypočítala ponuku
          </h2>
        </div>
      </div>

      <template v-if="hasCalculationTrace">
        <section class="offer-overview">
          <div class="offer-overview__item">
            <span>Výška úveru</span>
            <strong>{{ money(offer.pozicana_suma) }}</strong>
          </div>
          <div class="offer-overview__item">
            <span>Mesačná splátka</span>
            <strong>{{ money(offer.mesacna_splatka, 2) }}</strong>
          </div>
          <div class="offer-overview__item">
            <span>Sadzba / fixácia</span>
            <strong>{{ number(offer.sadzba, 2) }} % / {{ offer.fix }} r.</strong>
          </div>
          <div class="offer-overview__item">
            <span>Splatnosť</span>
            <strong>{{ offer.splatnost_r }} rokov</strong>
          </div>
          <div v-if="offerLtv !== null" class="offer-overview__item">
            <span>LTV ponuky</span>
            <strong>{{ number(offerLtv, 2) }} %</strong>
          </div>
        </section>

        <section class="calculation-section">
          <div class="section-heading">
            <div>
              <span class="section-heading__index">01</span>
              <h3>Akceptované príjmy</h3>
            </div>
            <strong>{{ money(trace.akceptovane_prijmy_mo, 2) }} / mes.</strong>
          </div>
          <p class="section-description">
            Ide o mesačné príjmy, ktoré banka po vlastnom metodickom krátení použila najmä vo výpočte DSTI.
          </p>
          <client-dti-info-card :incomes="applicantIncomeRows" role="applicant" />
          <client-dti-info-card :incomes="coApplicantIncomeRows" role="coapplicant" />
          <div v-if="incomeRows.length === 0" class="empty-note">
            V ponuke nie je dostupný rozpis jednotlivých príjmov.
          </div>
        </section>

        <section class="calculation-section">
          <div class="section-heading">
            <div>
              <span class="section-heading__index">02</span>
              <h3>DSTI krok za krokom</h3>
            </div>
            <strong>maximum {{ money(trace.maximalna_nova_splatka_mo, 2) }}</strong>
          </div>

          <div class="formula-list">
            <div class="formula-row">
              <div class="formula-row__number">1</div>
              <div class="formula-row__copy">
                <strong>Akceptované čisté príjmy domácnosti</strong>
                <span>Súčet vyššie uvedených príjmov po metodickom krátení banky.</span>
              </div>
              <strong class="formula-row__value">{{ money(trace.akceptovane_prijmy_mo, 2) }}</strong>
            </div>

            <div class="formula-row">
              <div class="formula-row__number">2</div>
              <div class="formula-row__copy">
                <strong>Odpočet životného minima domácnosti</strong>
                <span>{{ lifeMinimumExplanation }}</span>
              </div>
              <strong class="formula-row__value formula-row__value--minus">− {{ money(trace.zivotne_minimum_mo, 2) }}</strong>
            </div>

            <div class="formula-row formula-row--subtotal">
              <div class="formula-row__number">=</div>
              <div class="formula-row__copy">
                <strong>Príjem po odpočítaní životného minima</strong>
                <span>{{ money(trace.akceptovane_prijmy_mo, 2) }} − {{ money(trace.zivotne_minimum_mo, 2) }}</span>
              </div>
              <strong class="formula-row__value">{{ money(trace.prijem_po_odcitani_zivotneho_minima_mo, 2) }}</strong>
            </div>

            <div class="formula-row">
              <div class="formula-row__number">3</div>
              <div class="formula-row__copy">
                <strong>{{ number(trace.dsti_percenta_z_prijmu, 0) }} % zo zostávajúceho príjmu</strong>
                <span>Maximálna časť príjmu, ktorú možno použiť na všetky mesačné splátky.</span>
              </div>
              <strong class="formula-row__value">{{ money(trace.limit_na_vsetky_splatky_mo, 2) }}</strong>
            </div>

            <div class="formula-row">
              <div class="formula-row__number">4</div>
              <div class="formula-row__copy">
                <strong>Odpočet existujúcich záväzkov</strong>
                <span v-if="trace.stresovany_vypocet && positive(trace.existujuce_splatky_mo)">
                  Evidované splátky {{ money(trace.existujuce_splatky_mo, 2) }} banka zvýšila o
                  {{ number(trace.stres_existujucich_splatok_percent, 0) }} % na {{ money(trace.existujuce_splatky_po_strese_mo, 2) }}.
                </span>
                <span v-else-if="positive(trace.existujuce_splatky_mo)">
                  Súčet mesačných splátok aktívnych záväzkov.
                </span>
                <span v-else>Vo výpočte nie sú odpočítané žiadne existujúce splátky.</span>
              </div>
              <strong class="formula-row__value formula-row__value--minus">− {{ money(trace.existujuce_splatky_po_strese_mo, 2) }}</strong>
            </div>

            <div v-if="debtRows.length" class="debt-breakdown">
              <div v-for="debt in debtRows" :key="`${debt.role}-${debt.id}`" class="debt-breakdown__item">
                <div>
                  <span>{{ debt.role === "applicant" ? "Žiadateľ" : "Spolužiadateľ" }}</span>
                  <strong>{{ debt.type }}</strong>
                </div>
                <div>
                  <span>Mesačná splátka</span>
                  <strong>{{ money(debt.monthlyPayment, 2) }}</strong>
                </div>
                <div v-if="debt.balance !== null">
                  <span>Záväzok pre DTI</span>
                  <strong>{{ money(debt.balance) }}</strong>
                </div>
              </div>
            </div>

            <div class="formula-result">
              <div>
                <span>Maximálna nová mesačná splátka podľa DSTI</span>
                <strong>{{ money(trace.maximalna_nova_splatka_mo, 2) }}</strong>
              </div>
              <div>
                <span>Splátka tejto ponuky</span>
                <strong>{{ money(offer.mesacna_splatka, 2) }}</strong>
              </div>
              <div :class="{'formula-result__warning': paymentReserve < 0}">
                <span>Rezerva do DSTI limitu</span>
                <strong>{{ money(paymentReserve, 2) }}</strong>
              </div>
            </div>
          </div>
        </section>

        <section v-if="loanLimits.length" class="calculation-section">
          <div class="section-heading">
            <div>
              <span class="section-heading__index">03</span>
              <h3>Čo obmedzilo maximálnu výšku úveru</h3>
            </div>
            <strong>{{ money(maxLoanValue) }}</strong>
          </div>
          <p class="section-description">
            DSTI určuje dostupnú mesačnú splátku. Banka ju následne porovná s kontrolným limitom DTI, LTV a obmedzeniami účelu; maximum zodpovedá najnižšiemu výsledku.
          </p>
          <div class="limit-list">
            <div
              v-for="limit in loanLimits"
              :key="limit.key"
              class="limit-row"
              :class="{'limit-row--active': limit.active}"
            >
              <div>
                <strong>{{ limit.label }}</strong>
                <span>{{ limit.description }}</span>
              </div>
              <div class="limit-row__value">
                <span v-if="limit.active">Limitujúci faktor</span>
                <strong>{{ money(limit.value) }}</strong>
              </div>
            </div>
          </div>
        </section>

        <section v-if="calculationParameters.length" class="calculation-section calculation-section--compact">
          <div class="section-heading">
            <div>
              <span class="section-heading__index">04</span>
              <h3>Parametre metodiky banky</h3>
            </div>
          </div>
          <div class="parameter-grid">
            <div v-for="parameter in calculationParameters" :key="parameter.label" class="parameter-card">
              <span>{{ parameter.label }}</span>
              <strong>{{ parameter.value }}</strong>
            </div>
          </div>
        </section>

        <section class="calculation-section calculation-summary">
          <div class="section-heading">
            <div>
              <span class="section-heading__index">05</span>
              <h3>Sumárny vzorec a záver</h3>
            </div>
          </div>

          <div class="summary-context">
            <div>
              <span>Domácnosť</span>
              <strong>{{ householdComposition }}</strong>
            </div>
            <div>
              <span>Životné minimum</span>
              <strong>{{ money(trace.zivotne_minimum_mo, 2) }}</strong>
              <small>{{ lifeMinimumExplanation }}</small>
            </div>
            <div>
              <span>Celková splatnosť</span>
              <strong>{{ summaryMaturity }}</strong>
            </div>
            <div>
              <span>Sadzba použitá pre DSTI</span>
              <strong>{{ summaryStressedRate }}</strong>
            </div>
          </div>

          <div class="summary-formula">
            <span>
              ({{ money(trace.akceptovane_prijmy_mo, 2) }} − {{ money(trace.zivotne_minimum_mo, 2) }})
              × {{ number(trace.dsti_percenta_z_prijmu, 0) }} %
              − {{ money(trace.existujuce_splatky_po_strese_mo, 2) }}
            </span>
            <strong>= {{ money(trace.maximalna_nova_splatka_mo, 2) }} maximálna nová splátka</strong>
          </div>

          <p v-if="dstiLoanLimit" class="summary-explanation">
            Pri splatnosti {{ summaryMaturity }} a stresovej sadzbe {{ summaryStressedRate }} zodpovedá táto dostupná
            splátka maximálnemu úveru <strong>{{ money(dstiLoanLimit.value) }}</strong> podľa DSTI.
          </p>

          <div class="summary-decision" :class="{'summary-decision--dsti': dstiIsLimiting}">
            <span>{{ dstiIsLimiting ? "Určujúci limit" : "Vyhodnotenie ponuky" }}</span>
            <strong>{{ summaryDecisionTitle }}</strong>
            <p>{{ summaryDecisionText }}</p>
          </div>
        </section>

        <div class="calculation-note">
          Výpočet je informatívny a vychádza z údajov evidovaných v tejto hypotéke a z aktuálne nastavenej metodiky banky.
          Konečné posúdenie vykonáva banka.
        </div>
      </template>

      <template v-else-if="traceLoading">
        <section class="calculation-section trace-loading">
          <v-progress-circular
            class="loading-spinner small transparent"
            indeterminate
          />
          <div>
            <strong>Načítavam presný DSTI výpočet</strong>
            <span>Ponuku prepočítavam s rovnakou bankou, fixáciou a splatnosťou.</span>
          </div>
        </section>
      </template>

      <template v-else>
        <section class="calculation-section">
          <div class="section-heading">
            <div>
              <span class="section-heading__index">01</span>
              <h3>Akceptované príjmy</h3>
            </div>
          </div>
          <client-dti-info-card :incomes="legacyIncomeRows.applicant" role="applicant" />
          <client-dti-info-card :incomes="legacyIncomeRows.coApplicant" role="coapplicant" />
          <div v-if="traceLoadFailed" class="empty-note">
            API tejto ponuky nevrátilo životné minimum alebo súčet existujúcich splátok. Bez týchto operandov
            nie je možné zostaviť presný DSTI vzorec.
          </div>
        </section>
      </template>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import translateFromDBValue from "@/mixins/translateFromDBValue";
import { getIncomeDTI, getMortgageApplicationForSpecificBank, getMortgageDTI } from "@/services/brokerService";
import ClientDtiInfoCard from "@/components/shared/PopupHandler/Popups/ClientDtiInfo/ClientDtiInfoCard.vue";

export default {
  name: "ClientDtiInfoPopUp",
  components: { ClientDtiInfoCard },
  mixins: [translateFromDBValue],
  props: {
    payload: [Object, String],
  },
  data() {
    return {
      detailedOffer: null,
      traceLoading: false,
      traceLoadFailed: false,
      legacyIncomeRows: {
        applicant: [],
        coApplicant: [],
      },
    };
  },
  computed: {
    ...mapGetters({
      coApplicantIncomes: "mortgageDetailState/getCoApplicantIncomes",
      applicantIncomes: "mortgageDetailState/getApplicantIncomes",
      coApplicantDebts: "mortgageDetailState/getCoApplicantDebts",
      applicantDebts: "mortgageDetailState/getApplicantDebts",
      mortgage: "mortgageDetailState/getMortgage",
      amounts: "mortgageDetailState/getAmounts",
      offersData: "mortgageDetailState/getOffersData",
    }),
    bankKey() {
      return typeof this.payload === "string" ? this.payload : this.payload?.bankKey;
    },
    sourceOffer() {
      return typeof this.payload === "object" && this.payload?.offer ? this.payload.offer : {};
    },
    offer() {
      return this.detailedOffer || this.sourceOffer;
    },
    bankName() {
      return this.bankKey ? this.translateBankFromCodeName(this.bankKey) : "banka";
    },
    dtiResult() {
      return this.offer?.extra?.dti_result || null;
    },
    dtiExtra() {
      return this.dtiResult?.extra || {};
    },
    trace() {
      return this.dtiExtra?.vypocet_dsti || {};
    },
    hasCalculationTrace() {
      return this.asNumber(this.trace.maximalna_nova_splatka_mo) !== null;
    },
    incomeRows() {
      if (!this.dtiResult) return [];
      return [
        ...this.buildIncomeRows(this.dtiExtra.ziadatel, "applicant", this.applicantIncomes),
        ...this.buildIncomeRows(this.dtiExtra.spoluziadatel, "coapplicant", this.coApplicantIncomes),
      ];
    },
    applicantIncomeRows() {
      return this.incomeRows.filter((income) => income.role === "applicant");
    },
    coApplicantIncomeRows() {
      return this.incomeRows.filter((income) => income.role === "coapplicant");
    },
    lifeMinimumExplanation() {
      const life = this.dtiExtra?.zivotne_minima_result?.extra || {};
      const parts = [];
      const applicant = this.asNumber(life?.ziadatel_zivotne_minima?.value);
      const coApplicant = this.asNumber(life?.spoluziadatel_zivotne_minima?.value);
      const children = this.asNumber(life?.deti_zivotne_minima);
      const childrenCount = this.asNumber(life?.pocet_deti_spolu) || 0;

      if (applicant !== null) parts.push(`žiadateľ ${this.money(applicant, 2)}`);
      if (coApplicant) parts.push(`spolužiadateľ ${this.money(coApplicant, 2)}`);
      if (childrenCount || children) parts.push(`${childrenCount} ${this.childLabel(childrenCount)} ${this.money(children || 0, 2)}`);

      return parts.length ? parts.join(" + ") : "Životné minimum podľa zloženia domácnosti.";
    },
    householdComposition() {
      const life = this.dtiExtra?.zivotne_minima_result?.extra || {};
      const childrenCount = this.asNumber(life?.pocet_deti_spolu) || 0;
      const parts = ["1 žiadateľ"];
      if (this.dtiExtra.spoluziadatel) parts.push("1 spolužiadateľ");
      parts.push(`${childrenCount} ${this.childLabel(childrenCount)}`);
      return parts.join(" + ");
    },
    paymentReserve() {
      return (this.asNumber(this.trace.maximalna_nova_splatka_mo) || 0) - (this.asNumber(this.offer.mesacna_splatka) || 0);
    },
    offerLtv() {
      return this.asNumber(this.offer?.extra?.ltv_sr?.value);
    },
    debtRows() {
      if (!this.dtiResult) return [];
      return [
        ...this.buildDebtRows(this.dtiExtra.ziadatel, "applicant", this.applicantDebts),
        ...this.buildDebtRows(this.dtiExtra.spoluziadatel, "coapplicant", this.coApplicantDebts),
      ];
    },
    maxLoanResult() {
      return this.offer?.extra?.max_uver_sr || null;
    },
    maxLoanValue() {
      return this.asNumber(this.maxLoanResult?.value);
    },
    loanLimits() {
      const limitExtra = this.maxLoanResult?.extra || {};
      const definitions = [
        ["podla_dsti_a_splatnosti", "DSTI – mesačná splátka", "Maximálny úver odvodený od dostupnej splátky a stresovej sadzby."],
        ["podla_dti", "DTI – celkové zadlženie", "Kontrolný strop celkového dlhu odvodený od akceptovaného ročného príjmu."],
        ["podla_ltv", "LTV – hodnota zabezpečenia", "Limit podľa hodnoty zakladaných nehnuteľností a povoleného LTV."],
        ["podla_ucelu", "Účel financovania", "Maximum povolené pre zvolený účel úveru."],
        ["podla_kombinacie_ucelov", "Kombinácia účelov", "Limit pri kombinácii nadobudnutia, refinancovania alebo bezúčelovej časti."],
      ];

      return definitions.reduce((limits, definition) => {
        const value = this.asNumber(limitExtra?.[definition[0]]?.value);
        if (value === null || value <= 0) return limits;
        limits.push({
          key: definition[0],
          label: definition[1],
          description: definition[2],
          value,
          active: this.maxLoanValue !== null && Math.abs(value - this.maxLoanValue) < 1,
        });
        return limits;
      }, []);
    },
    activeLoanLimit() {
      return this.loanLimits.find((limit) => limit.active) || null;
    },
    dstiLoanLimit() {
      return this.loanLimits.find((limit) => limit.key === "podla_dsti_a_splatnosti") || null;
    },
    dstiIsLimiting() {
      return this.activeLoanLimit?.key === "podla_dsti_a_splatnosti";
    },
    summaryMaturity() {
      const months = this.asNumber(this.maxLoanResult?.extra?.splatnost_m || this.dtiExtra.splatnost_m);
      return months === null ? "zvolená bankou" : `${this.number(months / 12, 0)} rokov (${this.number(months, 0)} mesiacov)`;
    },
    summaryStressedRate() {
      const limitExtra = this.maxLoanResult?.extra || {};
      const rate = this.asNumber(limitExtra.sadzba_plus_stress);
      return rate === null ? "použitej bankou" : `${this.number(rate, 2)} %`;
    },
    summaryDecisionTitle() {
      if (this.dstiIsLimiting) return "Maximálnu úverovú kapacitu určuje DSTI";
      if (this.activeLoanLimit) return `Najnižší limit je ${this.activeLoanLimit.label}`;
      return "Požadovaná splátka bola porovnaná s DSTI limitom";
    },
    summaryDecisionText() {
      const payment = this.money(this.offer.mesacna_splatka, 2);
      const reserve = this.money(this.paymentReserve, 2);
      const requestedLoan = this.money(this.offer.pozicana_suma);

      if (this.dstiIsLimiting) {
        return `DSTI je z porovnávaných úverových limitov najnižšie. Ponuka ${requestedLoan} má splátku ${payment}; do maximálnej dostupnej splátky zostáva rezerva ${reserve}.`;
      }
      if (this.activeLoanLimit && this.dstiLoanLimit) {
        return `DSTI by umožnilo úver do ${this.money(this.dstiLoanLimit.value)}, ale výsledné maximum znížil limit ${this.activeLoanLimit.label} na ${this.money(this.activeLoanLimit.value)}. Ponuka ${requestedLoan} má splátku ${payment} a DSTI rezervu ${reserve}.`;
      }
      return `Ponuka ${requestedLoan} má splátku ${payment}. Maximálna splátka podľa DSTI je ${this.money(this.trace.maximalna_nova_splatka_mo, 2)}, takže zostáva rezerva ${reserve}.`;
    },
    calculationParameters() {
      const parameters = [];
      const limitExtra = this.maxLoanResult?.extra || {};
      const maturity = this.asNumber(limitExtra.splatnost_m || this.dtiExtra.splatnost_m);
      const rate = this.asNumber(limitExtra.sadzba);
      const stress = this.asNumber(limitExtra.stress_sadzba);
      const stressedRate = this.asNumber(limitExtra.sadzba_plus_stress);
      const dti = this.asNumber(this.offer?.extra?.dti ?? this.dtiResult?.value);

      if (maturity !== null) parameters.push({ label: "Splatnosť vo výpočte", value: `${this.number(maturity / 12, 0)} rokov` });
      if (rate !== null) parameters.push({ label: "Ponuková sadzba", value: `${this.number(rate, 2)} %` });
      if (stress !== null) parameters.push({ label: "Úrokový stres", value: `+ ${this.number(stress, 2)} p. b.` });
      if (stressedRate !== null) parameters.push({ label: "Sadzba pre DSTI limit", value: `${this.number(stressedRate, 2)} %` });
      if (dti !== null) parameters.push({ label: "Dostupný limit podľa DTI", value: this.money(dti) });
      return parameters;
    },
  },
  async mounted() {
    if (this.hasCalculationTrace) return;

    const normalizedSourceOffer = this.withCalculationTrace(this.sourceOffer);
    if (normalizedSourceOffer) {
      this.detailedOffer = normalizedSourceOffer;
      return;
    }

    const traceLoaded = await this.loadDetailedOffer();
    if (!traceLoaded) await this.loadLegacyIncomes();
  },
  methods: {
    async loadDetailedOffer() {
      const mortgageId = this.mortgage?.id || Number(this.$route?.params?.hypoteka);
      let bankId = this.sourceOffer?.bank?.id;
      if (!bankId && this.bankKey) {
        try {
          bankId = this.translateBankFromCodeNameToId(this.bankKey);
        } catch (error) {
          bankId = null;
        }
      }

      const fix = Math.round(Number(this.sourceOffer?.fix || this.offersData?.fix || 3));
      const maturityYears = Number(this.sourceOffer?.splatnost_r);
      const maturityMonths = Number.isFinite(maturityYears)
        ? Math.round(maturityYears * 12)
        : Math.round(Number(this.offersData?.splatnost_m || 360));

      if (!mortgageId || !bankId || !fix || !maturityMonths) {
        this.traceLoadFailed = true;
        return false;
      }

      this.traceLoading = true;
      this.traceLoadFailed = false;
      try {
        const fetchedOffer = await getMortgageApplicationForSpecificBank(mortgageId, {
          bank: bankId,
          fix,
          splatnost_m: maturityMonths,
          suma_nadobudnutie: Math.round(Number(this.amounts?.suma_nadobudnutie || 0)),
          suma_refinancovanie: Math.round(Number(this.amounts?.suma_refinancovanie || 0)),
          suma_bezucel: Math.round(Number(this.amounts?.suma_bezucel || 0)),
        });

        const mergedOffer = {
          ...this.sourceOffer,
          ...fetchedOffer,
          bank: this.sourceOffer?.bank,
          extra: {
            ...(this.sourceOffer?.extra || {}),
            ...(fetchedOffer?.extra || {}),
          },
        };

        this.detailedOffer = this.withCalculationTrace(mergedOffer) || mergedOffer;
        let loaded = this.asNumber(
          this.detailedOffer?.extra?.dti_result?.extra?.vypocet_dsti?.maximalna_nova_splatka_mo,
        ) !== null;

        if (!loaded) loaded = await this.loadMortgageDtiResult(mortgageId, maturityMonths);

        this.traceLoadFailed = !loaded;
        return loaded;
      } catch (error) {
        const loaded = await this.loadMortgageDtiResult(mortgageId, maturityMonths);
        this.traceLoadFailed = !loaded;
        return loaded;
      } finally {
        this.traceLoading = false;
      }
    },
    async loadMortgageDtiResult(mortgageId, maturityMonths) {
      if (!mortgageId || !this.bankKey) return false;

      try {
        const dtiResults = await getMortgageDTI(mortgageId, {
          splatnost_m: maturityMonths,
          ucel: this.primaryPurpose(),
          suma: Math.round(Number(this.sourceOffer?.pozicana_suma || 0)),
        });
        const dtiResult = dtiResults?.[this.bankKey];
        if (!dtiResult) return false;

        const normalizedOffer = this.withCalculationTrace({
          ...this.sourceOffer,
          extra: {
            ...(this.sourceOffer?.extra || {}),
            dti_result: dtiResult,
          },
        });
        if (!normalizedOffer) return false;

        this.detailedOffer = normalizedOffer;
        return true;
      } catch (error) {
        return false;
      }
    },
    primaryPurpose() {
      const amounts = this.amounts || {};
      if (Number(amounts.suma_nadobudnutie || 0) > 0) return "nadobudnutie";
      if (Number(amounts.suma_refinancovanie || 0) > 0) return "refinancovanie";
      return "bezucel";
    },
    withCalculationTrace(offer) {
      const dtiResult = offer?.extra?.dti_result;
      if (!dtiResult) return null;

      const existingTrace = dtiResult?.extra?.vypocet_dsti;
      if (this.asNumber(existingTrace?.maximalna_nova_splatka_mo) !== null) return offer;

      const trace = this.buildTraceFromLegacyDti(dtiResult);
      if (!trace) return null;

      return {
        ...offer,
        extra: {
          ...(offer.extra || {}),
          dti_result: {
            ...dtiResult,
            extra: {
              ...(dtiResult.extra || {}),
              vypocet_dsti: trace,
            },
          },
        },
      };
    },
    buildTraceFromLegacyDti(dtiResult) {
      const extra = dtiResult?.extra || {};
      const acceptedIncome = this.firstNumber(
        this.addNumbers(
          this.clientAcceptedIncome(extra.ziadatel),
          this.clientAcceptedIncome(extra.spoluziadatel),
        ),
        extra.dsti_hrube,
      );
      const lifeMinimum = this.firstNumber(
        extra.zivotne_minima_result?.value,
        extra.zivotne_minima,
      );
      const afterLifeMinimum = this.firstNumber(
        extra.prijmy_minus_splatky_mo,
        acceptedIncome !== null && lifeMinimum !== null ? acceptedIncome - lifeMinimum : null,
      );
      const originalPayments = this.firstNumber(
        extra.mesacne_splatky,
        this.addNumbers(
          extra.ziadatel?.extra?.mesacne_splatky,
          extra.spoluziadatel?.extra?.mesacne_splatky,
        ),
        0,
      );
      const maximumNewPayment = this.firstNumber(
        dtiResult.dsti,
        extra.dsti,
        extra.dsti_result?.value,
        dtiResult.dsti_result?.value,
      );

      if ([acceptedIncome, lifeMinimum, afterLifeMinimum, maximumNewPayment].some((value) => value === null)) {
        return null;
      }

      // Older API responses already contain all operands, only not grouped in
      // `vypocet_dsti`. Derive the configured coefficient from those returned
      // operands so the UI does not guess or hard-code 60 %.
      const stressedPayments = originalPayments * 1.2;
      const allPaymentsLimit = maximumNewPayment + stressedPayments;
      const percentage = afterLifeMinimum > 0 ? (allPaymentsLimit / afterLifeMinimum) * 100 : 0;

      return {
        akceptovane_prijmy_mo: acceptedIncome,
        zivotne_minimum_mo: lifeMinimum,
        prijem_po_odcitani_zivotneho_minima_mo: afterLifeMinimum,
        dsti_percenta_z_prijmu: percentage,
        limit_na_vsetky_splatky_mo: allPaymentsLimit,
        existujuce_splatky_mo: originalPayments,
        stres_existujucich_splatok_percent: originalPayments > 0 ? 20 : 0,
        existujuce_splatky_po_strese_mo: stressedPayments,
        maximalna_nova_splatka_mo: maximumNewPayment,
        stresovany_vypocet: true,
      };
    },
    clientAcceptedIncome(clientResult) {
      if (!clientResult) return null;
      const directValue = this.asNumber(clientResult?.extra?.prijem_mo);
      if (directValue !== null) return directValue;

      const partials = Object.values(clientResult?.extra?.prijmy || {});
      if (!partials.length) return null;
      return partials.reduce((sum, partial) => sum + (this.asNumber(partial?.extra?.prijem_mo) || 0), 0);
    },
    addNumbers(...values) {
      const numbers = values.map((value) => this.asNumber(value)).filter((value) => value !== null);
      return numbers.length ? numbers.reduce((sum, value) => sum + value, 0) : null;
    },
    firstNumber(...values) {
      for (const value of values) {
        const numeric = this.asNumber(value);
        if (numeric !== null) return numeric;
      }
      return null;
    },
    asNumber(value) {
      if (value === null || value === undefined || value === "") return null;
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    },
    number(value, decimals = 0) {
      const numeric = this.asNumber(value);
      if (numeric === null) return "—";
      return new Intl.NumberFormat("sk-SK", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(numeric);
    },
    money(value, decimals = 0) {
      const numeric = this.asNumber(value);
      if (numeric === null) return "—";
      return `${this.number(numeric, decimals)} EUR`;
    },
    positive(value) {
      return (this.asNumber(value) || 0) > 0;
    },
    childLabel(count) {
      if (count === 1) return "dieťa";
      if (count > 1 && count < 5) return "deti";
      return "detí";
    },
    buildIncomeRows(clientResult, role, sourceIncomes) {
      const partialIncomes = clientResult?.extra?.prijmy || {};
      return Object.entries(partialIncomes).map(([identifier, partial]) => {
        const income = (sourceIncomes || []).find((item) => String(item.id) === String(identifier));
        const type = income ? this.translateIncomeType(income.druh) : "Príjem";
        const employer = income?.zamestnavatel?.name || null;
        return {
          id: identifier,
          role,
          type,
          employer,
          success: partial?.success !== false,
          acceptedMonthly: this.asNumber(partial?.extra?.prijem_mo),
          annualDebtCapacity: this.asNumber(partial?.value),
          facts: this.buildIncomeFacts(type, partial?.extra || {}),
          details: partial?.details || [],
        };
      });
    },
    buildDebtRows(clientResult, role, sourceDebts) {
      const partialDebts = clientResult?.extra?.dlhy || {};
      return Object.entries(partialDebts).reduce((rows, [identifier, partial]) => {
        if (partial?.success === false) return rows;
        const debt = (sourceDebts || []).find((item) => String(item.id) === String(identifier));
        rows.push({
          id: identifier,
          role,
          type: debt?.typ ? (this.translateDebtType(debt.typ) || "Záväzok") : "Záväzok",
          monthlyPayment: this.asNumber(partial?.extra?.mesacne_splatky),
          balance: this.asNumber(partial?.value) === null ? null : Math.abs(this.asNumber(partial.value)),
        });
        return rows;
      }, []);
    },
    buildIncomeFacts(type, extra) {
      const facts = [];
      const calculation = extra.pouzity_vypocet || extra.calculation_used;

      if (type === "Zamestnanie") {
        const incomes = Array.isArray(extra.zoznam_prijmov) ? extra.zoznam_prijmov.filter((value) => this.asNumber(value) !== null) : [];
        const average = this.asNumber(extra?.priemerny_akc_cisty_prijem_mo?.value);
        if (incomes.length) facts.push(`Banka vyhodnotila ${incomes.length} ${incomes.length === 1 ? "mesiac" : "mesiacov"} príjmu.`);
        if (average !== null) facts.push(`Akceptovaný priemer čistej mzdy je ${this.money(average, 2)} mesačne.`);
        const diets = this.asNumber(extra.diety_hrube_mo);
        if (diets) facts.push(`Evidovaný priemer diét je ${this.money(diets, 2)} mesačne; banka ho spracovala podľa vlastnej metodiky.`);
      } else if (type === "Živnosť") {
        const turnover = this.asNumber(extra.trzby);
        const share = this.asNumber(extra.akceptovany_podiel_z_obratu);
        if (calculation) facts.push(`Použitý bol výpočet ${this.calculationLabel(calculation)}.`);
        if (turnover !== null) facts.push(`Ročný obrat: ${this.money(turnover, 2)}.`);
        if (share !== null) facts.push(`Banka akceptovala ${this.number(share, 2)} % z obratu.`);
        const taxBase = this.asNumber(extra.zaklad_dane);
        if (taxBase !== null) facts.push(`Základ dane vo výpočte: ${this.money(taxBase, 2)}.`);
      } else if (type === "Sro") {
        const turnover = this.asNumber(extra.trzby);
        const profit = this.asNumber(extra.zisk);
        const ownership = this.asNumber(extra.podiel);
        const acceptedShare = this.asNumber(extra.akceptovany_podiel_z_trzieb);
        if (calculation) facts.push(`Použitý bol výpočet ${this.calculationLabel(calculation)}.`);
        if (turnover !== null) facts.push(`Ročné tržby spoločnosti: ${this.money(turnover, 2)}.`);
        if (acceptedShare !== null) facts.push(`Banka akceptovala ${this.number(acceptedShare, 2)} % z tržieb spoločnosti.`);
        if (profit !== null) facts.push(`Evidovaný zisk spoločnosti: ${this.money(profit, 2)}.`);
        if (ownership !== null) facts.push(`Podiel žiadateľa v spoločnosti: ${this.number(ownership, 2)} %.`);
      } else if (type === "Prenájom") {
        const source = this.asNumber(extra.suma);
        const share = this.asNumber(extra.akceptovany_podiel_z_prenajmu);
        if (calculation) facts.push(`Použitý bol ${String(calculation).toLowerCase()}.`);
        if (source !== null) facts.push(`Doložený príjem z prenájmu: ${this.money(source, 2)} mesačne.`);
        if (share !== null) facts.push(`Banka akceptovala najviac ${this.number(share, 2)} % z príjmu z prenájmu.`);
      } else {
        const source = this.asNumber(extra.suma);
        if (source !== null) facts.push(`Doložená mesačná suma: ${this.money(source, 2)}.`);
      }

      return facts;
    },
    calculationLabel(value) {
      const labels = {
        obrat: "z obratu",
        zisk: "zo zisku",
        zaklad_dane: "zo základu dane",
      };
      return labels[value] || String(value).toLowerCase();
    },
    async loadLegacyIncomes() {
      await Promise.all([
        this.loadLegacyIncomeGroup(this.applicantIncomes, "applicant"),
        this.loadLegacyIncomeGroup(this.coApplicantIncomes, "coApplicant"),
      ]);
    },
    async loadLegacyIncomeGroup(incomes, target) {
      const rows = [];
      for (const income of (incomes || []).filter((item) => item.is_active === true)) {
        try {
          const bankResults = await getIncomeDTI(income.id);
          const partial = bankResults?.[this.bankKey];
          if (!partial) continue;
          const type = this.translateIncomeType(income.druh) || "Príjem";
          rows.push({
            id: income.id,
            role: target === "applicant" ? "applicant" : "coapplicant",
            type,
            employer: income?.zamestnavatel?.name || null,
            success: partial.success !== false,
            acceptedMonthly: this.asNumber(partial?.extra?.prijem_mo),
            annualDebtCapacity: this.asNumber(partial.value),
            facts: this.buildIncomeFacts(type, partial.extra || {}),
            details: partial.details || [],
          });
        } catch (error) {
          // The detailed offer trace is preferred. A failed legacy request
          // should not prevent the popup from opening.
        }
      }
      this.legacyIncomeRows[target] = rows;
    },
    closePopup() {
      this.$store.commit("popupManagerState/popItemFromPopupOrder");
    },
  },
};
</script>

<style scoped>
.calculation-popup {
  width: min(46vw, 760px);
  background: var(--app-bg);
}

.calculation-popup__content {
  gap: 22px;
  padding: 28px 28px 72px;
}

.calculation-popup__header {
  align-items: flex-start;
  gap: 14px;
}

.calculation-popup__eyebrow,
.section-heading__index {
  color: var(--app-primary);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.calculation-popup__title {
  margin: 4px 0 0;
  color: var(--app-text);
  font-size: clamp(22px, 2vw, 32px);
  line-height: 1.12;
}

.offer-overview,
.parameter-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.offer-overview__item,
.parameter-card {
  display: flex;
  min-width: 0;
  padding: 14px 16px;
  flex-direction: column;
  gap: 5px;
  border: 1px solid var(--app-border);
  border-radius: 14px;
  background: var(--app-surface);
}

.offer-overview__item span,
.parameter-card span {
  color: var(--app-text-muted);
  font-size: 12px;
}

.offer-overview__item strong,
.parameter-card strong {
  color: var(--app-text);
  font-size: 15px;
}

.calculation-section {
  padding: 20px;
  border: 1px solid var(--app-border);
  border-radius: 18px;
  background: var(--app-surface);
}

.trace-loading {
  display: flex;
  min-height: 150px;
  align-items: center;
  justify-content: center;
  gap: 14px;
}

.trace-loading > div {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.trace-loading strong {
  color: var(--app-text);
  font-size: 14px;
}

.trace-loading span {
  color: var(--app-text-muted);
  font-size: 12px;
}

.section-heading {
  display: flex;
  gap: 18px;
  align-items: flex-start;
  justify-content: space-between;
}

.section-heading > div {
  display: flex;
  gap: 10px;
  align-items: baseline;
}

.section-heading h3 {
  margin: 0;
  color: var(--app-text);
  font-size: 18px;
  line-height: 1.25;
}

.section-heading > strong {
  color: var(--app-primary);
  font-size: 14px;
  text-align: right;
}

.section-description {
  margin: 8px 0 16px 31px;
  color: var(--app-text-muted);
  font-size: 13px;
  line-height: 1.5;
}

.formula-list,
.limit-list {
  display: flex;
  margin-top: 16px;
  flex-direction: column;
  gap: 9px;
}

.formula-row,
.limit-row {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 12px;
  padding: 14px;
  align-items: center;
  border: 1px solid var(--app-border);
  border-radius: 13px;
  background: var(--app-surface-muted);
}

.formula-row__number {
  display: inline-flex;
  width: 28px;
  height: 28px;
  align-items: center;
  justify-content: center;
  border-radius: 9px;
  background: var(--app-primary-soft);
  color: var(--app-primary);
  font-size: 12px;
  font-weight: 800;
}

.formula-row__copy,
.limit-row > div:first-child {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 3px;
}

.formula-row__copy strong,
.limit-row strong {
  color: var(--app-text);
  font-size: 13px;
}

.formula-row__copy span,
.limit-row span {
  color: var(--app-text-muted);
  font-size: 11px;
  line-height: 1.45;
}

.formula-row__value {
  color: var(--app-text);
  font-size: 13px;
  white-space: nowrap;
}

.formula-row__value--minus {
  color: #df6673;
}

.formula-row--subtotal {
  border-color: color-mix(in srgb, var(--app-primary) 42%, var(--app-border));
}

.formula-result {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  margin-top: 6px;
}

.debt-breakdown {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.debt-breakdown__item {
  display: flex;
  padding: 12px;
  flex-wrap: wrap;
  gap: 12px 18px;
  border: 1px dashed var(--app-border);
  border-radius: 12px;
}

.debt-breakdown__item > div {
  display: flex;
  min-width: 100px;
  flex: 1;
  flex-direction: column;
  gap: 2px;
}

.debt-breakdown__item span {
  color: var(--app-text-muted);
  font-size: 10px;
}

.debt-breakdown__item strong {
  color: var(--app-text);
  font-size: 12px;
}

.formula-result > div {
  display: flex;
  padding: 14px;
  flex-direction: column;
  gap: 6px;
  border-radius: 12px;
  background: var(--app-primary-soft);
}

.formula-result span {
  color: var(--app-text-muted);
  font-size: 10px;
  line-height: 1.3;
}

.formula-result strong {
  color: var(--app-text);
  font-size: 14px;
}

.formula-result__warning strong {
  color: #df6673;
}

.calculation-summary {
  border-color: color-mix(in srgb, var(--app-primary) 55%, var(--app-border));
}

.summary-formula {
  display: flex;
  margin-top: 16px;
  padding: 16px;
  flex-direction: column;
  gap: 8px;
  border-radius: 14px;
  background: var(--app-primary-soft);
}

.summary-context {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin-top: 16px;
}

.summary-context > div {
  display: flex;
  min-width: 0;
  padding: 12px 14px;
  flex-direction: column;
  gap: 3px;
  border: 1px solid var(--app-border);
  border-radius: 12px;
  background: var(--app-surface-muted);
}

.summary-context span,
.summary-context small {
  color: var(--app-text-muted);
  font-size: 10px;
  line-height: 1.4;
}

.summary-context strong {
  color: var(--app-text);
  font-size: 13px;
}

.summary-formula span,
.summary-explanation,
.summary-decision p {
  color: var(--app-text-muted);
  font-size: 12px;
  line-height: 1.55;
}

.summary-formula strong {
  color: var(--app-primary);
  font-size: 16px;
}

.summary-explanation {
  margin: 14px 0 0;
}

.summary-explanation strong {
  color: var(--app-text);
}

.summary-decision {
  display: flex;
  margin-top: 14px;
  padding: 15px 16px;
  flex-direction: column;
  gap: 4px;
  border: 1px solid var(--app-border);
  border-radius: 14px;
  background: var(--app-surface-muted);
}

.summary-decision--dsti {
  border-color: var(--app-primary);
}

.summary-decision > span {
  color: var(--app-primary);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.summary-decision > strong {
  color: var(--app-text);
  font-size: 15px;
}

.summary-decision p {
  margin: 2px 0 0;
}

.limit-row {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
}

.limit-row--active {
  border-color: var(--app-primary);
  background: var(--app-primary-soft);
}

.limit-row__value {
  display: flex;
  padding-top: 10px;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  border-top: 1px solid var(--app-border);
  text-align: left;
}

.limit-row__value span {
  color: var(--app-primary);
  font-weight: 800;
  text-transform: uppercase;
}

.limit-row__value strong {
  font-size: 16px;
  line-height: 1.2;
}

.empty-note,
.calculation-note {
  padding: 14px 16px;
  border: 1px dashed var(--app-border);
  border-radius: 12px;
  color: var(--app-text-muted);
  font-size: 12px;
  line-height: 1.55;
}

.empty-note {
  margin-top: 12px;
}

@media (max-width: 900px) {
  .calculation-popup {
    width: 100vw;
  }
}

@media (max-width: 560px) {
  .calculation-popup__content {
    padding: 20px 16px 64px;
  }

  .offer-overview,
  .parameter-grid,
  .summary-context,
  .formula-result,
  .debt-breakdown {
    grid-template-columns: 1fr;
  }

  .calculation-section {
    padding: 16px;
  }

  .section-heading,
  .formula-row,
  .limit-row {
    grid-template-columns: auto minmax(0, 1fr);
  }

  .section-heading {
    flex-direction: column;
  }

  .formula-row__value,
  .limit-row__value {
    grid-column: 2;
    text-align: left;
    align-items: flex-start;
  }
}
</style>
